import {
  Activity,
  Users,
  Wallet,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import { formatNumber } from "@/lib/utils/format";
import { t } from "@/lib/i18n/messages";
import type { Locale } from "@/types/auth";
import { ErrorMessage } from "@/components/ui/error-message";
import { Skeleton } from "@/components/ui/skeleton";
import type { InitiativeStatistics } from "@/types/initiative";
import { cn } from "@/lib/utils";

interface InitiativesStatsProps {
  locale: Locale;
  data: InitiativeStatistics | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

interface KpiCellData {
  caption: string;
  value: string;
  icon: LucideIcon;
}

export function InitiativesStats({
  locale,
  data,
  loading,
  error,
  refetch,
}: InitiativesStatsProps) {
  const isRTL = locale === "ar";

  if (loading) {
    return <KpiStripSkeleton />;
  }

  if (error) {
    return (
      <ErrorMessage
        title={t(locale, "error")}
        message={error}
        onRetry={refetch}
        retryLabel={t(locale, "retry")}
      />
    );
  }

  if (!data) return null;

  const cells: KpiCellData[] = [
    {
      caption: isRTL ? "المبادرات النشطة" : "ACTIVE",
      value: formatNumber(data.active_initiatives, locale),
      icon: Activity,
    },
    {
      caption: isRTL ? "المشاركون" : "PARTICIPANTS",
      value: formatNumber(data.participants_rewarded, locale),
      icon: Users,
    },
    {
      caption: isRTL ? "ممكِّنون" : "ENABLERS",
      value: formatNumber(data.total_enablers, locale),
      icon: Wallet,
    },
    {
      caption: isRTL ? "المجموع الكلي" : "TOTAL",
      value: formatNumber(data.total_initiatives, locale),
      icon: TrendingUp,
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
      {cells.map((cell) => (
        <KpiCell key={cell.caption} {...cell} />
      ))}
    </div>
  );
}

function KpiCell({ caption, value, icon: Icon }: KpiCellData) {
  return (
    <div
      className={cn(
        "bg-card p-5 md:p-6 rounded-lg shadow-sm border border-border",
        "flex flex-col gap-3 min-h-[112px]",
        "transition-all duration-base hover:shadow-md hover:border-border-hover",
      )}
    >
      <div className="flex items-center justify-between">
        <span className="font-sans text-[10px] tracking-[0.25em] text-foreground-secondary uppercase">
          {caption}
        </span>
        <span
          className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 text-primary"
          aria-hidden="true"
        >
          <Icon className="w-3.5 h-3.5" strokeWidth={1.8} />
        </span>
      </div>
      <p
        className={cn(
          "font-display font-bold text-3xl md:text-4xl tracking-tight",
          "tabular-nums leading-none text-foreground",
        )}
      >
        {value}
      </p>
    </div>
  );
}

function KpiStripSkeleton() {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="bg-card p-5 md:p-6 rounded-lg shadow-sm border border-border flex flex-col gap-3 min-h-[112px]"
        >
          <div className="flex items-center justify-between">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="w-7 h-7 rounded-lg" />
          </div>
          <Skeleton className="h-9 w-24" />
        </div>
      ))}
    </div>
  );
}
